/**
 * Правка на месте (как `editor.js` прежней панели): щелчок по тексту
 * превращает его в поле, blur или Enter (в многострочном — Ctrl+Enter)
 * сохраняют, Escape отменяет. Последнее слово — за сервером: показываем то,
 * что он вернул, а не то, что набрали, — он мог обрезать или отказать.
 */

import clsx from "clsx";
import type { KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { errorText } from "@/lib/project";

export function InlineText({
  value,
  placeholder,
  multiline = false,
  onSave,
  className,
  annot,
}: {
  value: string | null;
  placeholder: string;
  multiline?: boolean;
  /** Сохранить и вернуть то, что сохранил сервер. */
  onSave: (value: string) => Promise<string>;
  className?: string | undefined;
  annot?: string | undefined;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const field = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const before = value ?? "";

  useEffect(() => {
    const el = field.current;
    if (draft === null || !el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    // Фокус ставится один раз — при входе в правку, а не на каждую букву.
  }, [draft === null]);

  useEffect(() => {
    const el = field.current;
    if (!multiline || !el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [draft, multiline]);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 900);
    return () => clearTimeout(timer);
  }, [saved]);

  const finish = async (save: boolean) => {
    if (draft === null) return;
    const text = draft.trim();
    setDraft(null);
    if (!save || text === before.trim()) return;
    setSaving(text);
    setError("");
    try {
      await onSave(text);
      setSaved(true);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(null);
    }
  };

  if (draft !== null) {
    const common = {
      ref: field,
      value: draft,
      onChange: (e: { target: { value: string } }) => setDraft(e.target.value),
      onBlur: () => void finish(true),
      onKeyDown: (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.preventDefault();
          void finish(false);
        } else if (e.key === "Enter" && (!multiline || e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          void finish(true);
        }
      },
      placeholder,
      className:
        "w-full rounded-[var(--radius-ui)] border border-accent bg-bg px-2 py-1 text-fg outline-none placeholder:text-fg-faint",
    };
    return (
      <div data-annot={annot} className={className}>
        {multiline ? <textarea rows={3} {...common} className={clsx(common.className, "resize-y leading-[1.5]")} /> : <input type="text" {...common} />}
        <div className="mt-0.5 text-[11px] text-fg-faint">
          {multiline ? "Ctrl+Enter — сохранить" : "Enter — сохранить"} · Esc — отменить
        </div>
      </div>
    );
  }

  const shown = saving ?? before;
  return (
    <div data-annot={annot} className={className}>
      <div
        role="button"
        tabIndex={0}
        title="щёлкните, чтобы править"
        onClick={() => setDraft(shown)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            setDraft(shown);
          }
        }}
        className={clsx(
          "cursor-text rounded-[var(--radius-ui)] px-1 -mx-1 transition-colors hover:bg-bg-hover",
          multiline && "whitespace-pre-wrap",
          saved && "bg-ok-bg",
          saving !== null && "opacity-60",
          !shown && "text-fg-faint italic",
        )}
      >
        {shown || placeholder}
      </div>
      {error ? <div className="mt-1 text-[12px] text-err">{error}</div> : null}
    </div>
  );
}
